<script setup lang="ts">
import { type PropType, computed } from "vue";
import type { CompositionSpacerSize } from "../../..";
import CompositionSpacer from "../Composition/CompositionSpacer.vue";

const props = defineProps({
  /*
      The direction of the key/value pair.
      values: horizontal, vertical
    */
  direction: {
    type: String,
    required: true,
  },

  /*
      Separates all children with a comma
    */
  commaSeparate: {
    type: Boolean,
    default: false,
  },

  /*
      Override default spacing size
      Use "none" to disable
    */
  spacingSize: {
    type: String as PropType<CompositionSpacerSize | "none">,
    default: "tiny",
  },

  /*
      Align items: flex-start
    */
  alignStart: {
    type: Boolean,
    default: false,
  },

  /*
      Align items: flex-end
    */
  justifyEnd: {
    type: Boolean,
    default: false,
  },

  /*
      Render the key/value pair as a label
    */
  renderAsLabel: {
    type: Boolean,
    default: false,
  },

  /*
      Namespace for label color
    */
  labelColorNamespace: {
    type: String,
    default: "black",
  },
});

const labelColorForeground = computed(
  () => `--color-${props.labelColorNamespace}-200`
);
const labelColorBackground = computed(
  () => `--color-${props.labelColorNamespace}-100`
);
</script>

<template>
  <div
    :class="[
      'key-value-pair',
      $style.container,
      $style[direction],
      alignStart && $style.alignStart,
      justifyEnd && $style.justifyEnd,
      renderAsLabel && $style.renderAsLabel,
    ]"
    :style="{
      '--label-color-foreground': `var(${labelColorForeground})`,
      '--label-color-background': `var(${labelColorBackground})`,
    }"
  >
    <div :class="[$style.containerStart]">
      <slot name="containerStart" />
    </div>

    <CompositionSpacer
      v-if="direction === 'horizontal' && spacingSize !== 'none'"
      direction="horizontal"
      :size="spacingSize"
    />

    <CompositionSpacer
      v-if="direction === 'vertical' && spacingSize !== 'none'"
      direction="vertical"
      :size="spacingSize"
    />

    <div
      :class="[
        $style.containerEnd,
        commaSeparate ? $style.containerCommaSeparate : '',
        commaSeparate ? $style.commaSeparate : '',
      ]"
    >
      <slot name="containerEnd" />
    </div>
  </div>
</template>

<style module>
.container {
  display: inline-flex;
  align-items: inherit;
}
.containerStart {
  display: inline-flex;
  align-items: inherit;
  position: relative;
}

.containerEnd {
  display: inline-flex;
  align-items: inherit;
  position: relative;
}

.containerCommaSeparate {
  display: inline-flex;
  flex-wrap: wrap;
}

.vertical {
  flex-direction: column;
}

.horizontal {
  flex-direction: row;
  align-items: center;
}

.alignStart {
  align-items: flex-start;
}

.justifyEnd {
  justify-content: flex-end;
}

.renderAsLabel {
  background-color: var(--label-color-background);
  color: var(--label-color-foreground);
  padding: 0.125rem 0.5rem;
  border: 1px solid var(--label-color-foreground);
}

.commaSeparate > *:not(:empty):not(:last-child):after {
  color: var(--color-purple-50);
  content: ",\00a0";
}
</style>
